import {
  whiteShades,
  primaryColors,
} from "../../assets/styles/helpers/colors.styles";
import { PieChart as RechartsPieChart, Pie, Cell } from "recharts";
import ProgressBar from "../ProgressBar/ProgressBar";
import styles from "./PieChart.module.scss";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Above this many parts the ring is unreadable, and the segment count is
 * clamped anyway — so it would also be lying. Past it we show the same
 * figures on the linear bar used for the bill instead.
 */
const MAX_PIE_SEGMENTS = 32;

interface PieChartProps {
  numberOfParts: number;
  highlightCount: number;
  pricePerPart: number;
}

const PieChart = (props: PieChartProps) => {
  const { numberOfParts, highlightCount, pricePerPart } = props;

  const [limitedNumberOfParts, setLimitedNumberOfParts] = useState(1);
  const [limitedHighlightCount, setLimitedHighlightCount] = useState(1);
  const { t } = useTranslation();

  useEffect(() => {
    if (numberOfParts < 1) {
      setLimitedNumberOfParts(1);
    } else if (numberOfParts > 32) {
      setLimitedNumberOfParts(32);
    } else {
      setLimitedNumberOfParts(numberOfParts);
    }
  }, [numberOfParts]);

  useEffect(() => {
    if (highlightCount < 1) {
      setLimitedHighlightCount(1);
    } else if (highlightCount > 32) {
      setLimitedHighlightCount(32);
    } else {
      setLimitedHighlightCount(highlightCount);
    }
  }, [highlightCount]);

  const shareLabel = (
    <div className={styles.pieChartTitleContainer}>
      <p className={`body-medium ${styles.pieChartPrice}`}>
        {pricePerPart.toFixed(2)} zł
      </p>
      <p className={`body-bold-medium ${styles.pieChartTitle}`}>
        {t("gradientDrawer.divideBillEqually.amountToShare")}
      </p>
    </div>
  );

  if (numberOfParts > MAX_PIE_SEGMENTS) {
    const progress =
      numberOfParts > 0
        ? Math.min(100, Math.max(0, (highlightCount / numberOfParts) * 100))
        : 0;

    return (
      <div className={styles.linearShareWrapper}>
        {shareLabel}
        <ProgressBar progress={progress} />
      </div>
    );
  }

  const data = Array.from({ length: limitedNumberOfParts }, (_, i) => ({
    id: i,
    value: 100 / limitedNumberOfParts,
    label: `Part ${i + 1}`,
    color:
      i < limitedHighlightCount ? primaryColors.orange : whiteShades.shade1,
  }));

  return (
    <div className={styles.pieChartWrapper}>
      {shareLabel}
      <RechartsPieChart width={208} height={208}>
        <Pie
          data={data}
          dataKey="value"
          nameKey="label"
          cx="50%"
          cy="50%"
          innerRadius={100}
          outerRadius={104}
          paddingAngle={4}
          animationEasing="ease-in-out"
          animationBegin={0}
          stroke={"none"}
          startAngle={90}
          endAngle={-270}
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color} />
          ))}
        </Pie>
      </RechartsPieChart>
    </div>
  );
};

export default PieChart;
